import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { EMPTY, from } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { SelectControlComponent } from 'src/app/features/shared/controls/container/select-control/select-control.component';
import { autocomplete, IAutoComplete } from 'src/app/features/shared/controls/models/auto-complete.model';
import { billingDryRunDto, IBillingDryRunDto } from 'src/assets/common/models/billing-dry-run-dto.g';
import { billingDryRunLineDto } from 'src/assets/common/models/billing-dry-run-line-dto.g';
import { billingStorageUsageDto } from 'src/assets/common/models/billing-storage-usage-dto.g';
import { AppDatePipe } from 'src/app/infrastructure/pipes/app-date.pipe';
import { creditDomainDto, ICreditDomainDto } from 'src/assets/common/models/credit-domain-dto.g';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { unwrapData } from 'src/app/infrastructure/functions/unwrap-data.operator';
import { bytes_format, number_format, period_label, period_list } from '../../models/credits-format';

const __kindLabel: Record<string, string> = {
    function: 'functie',
    storage: 'opslag',
    discount: 'korting',
};

// Proefberekening per domain en maand (BillingCalculator, creditplan fase 2): welke persoon met welke functie
// telde, hoeveel volle dagen, opslagblokken en korting. Boekt niets. Rekent ook vóór de startmaand van de
// afrekening (dan met de melding dat de maand niet gerekend wordt); de lopende maand = prognose tot gisteren.
@Component({
    selector: 'app-billing-dry-run',
    standalone: true,
    imports: [AppDatePipe, SelectControlComponent],
    templateUrl: './billing-dry-run.component.html',
    styleUrl: './billing-dry-run.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingDryRunComponent {
    private readonly __apiService = injectRequired(ApiService);
    private readonly __destroyRef = injectRequired(DestroyRef);

    // AdminPanel: het gekozen domain (api/Billing/dry-run). Tab credits van een domain / accountoverzicht: het eigen
    // lidmaatschap als context (api/DomainCredits/dry-run, claim DomainCredits/Page/Read).
    input_domain$$ = input<ICreditDomainDto | null>(null, { alias: 'domain' });
    input_dauGuid$$ = input<string | null>(null, { alias: 'dauGuid' });

    readonly billingDryRunDto = billingDryRunDto;
    readonly billingDryRunLineDto = billingDryRunLineDto;
    readonly number_format = number_format;
    readonly bytes_format = bytes_format;
    readonly billingStorageUsageDto = billingStorageUsageDto;
    kind_label = (kind: string | null) => __kindLabel[kind ?? ''] ?? kind ?? '';

    readonly domainGuid$$ = computed(() => this.input_domain$$()?.[creditDomainDto.domainGuid] ?? null);
    private readonly __key$$ = computed(() => this.input_dauGuid$$() ?? this.domainGuid$$());
    readonly list_period: IAutoComplete[] = period_list(-12, 0);

    readonly control_period = new FormControl<IAutoComplete | null>(this.list_period[0]);

    private readonly __result$$ = signal<IBillingDryRunDto | null>(null);
    readonly result$$ = this.__result$$.asReadonly();
    private readonly __loading$$ = signal(false);
    readonly loading$$ = this.__loading$$.asReadonly();

    readonly periodLabel$$ = computed(() => period_label(this.result$$()?.[billingDryRunDto.period]));
    // Dagmetingen van de opslag; de piek bepaalt de opslagkosten van de maand.
    readonly storage$$ = computed(() => this.result$$()?.[billingDryRunDto.storageUsages] ?? []);
    readonly storagePeak$$ = computed(() => Math.max(0, ...this.storage$$().map(u => u[billingStorageUsageDto.bytes] ?? 0)));

    compareWith = (a: IAutoComplete | null, b: IAutoComplete | null) => a?.[autocomplete.itemId] === b?.[autocomplete.itemId];

    protected readonly _text = {
        title: 'proefberekening',
        info: 'wat de afrekening voor dit domain en deze maand zou zijn. Er wordt niets geboekt.',
        period: 'maand',
        notBillable: 'deze maand valt vóór de startmaand van de afrekening en wordt niet gerekend',
        current: 'lopende maand: prognose tot en met gisteren',
        charged: 'al geboekt',
        kind: 'soort',
        user: 'persoon',
        function: 'functie',
        days: 'dagen',
        blocks: 'blokken',
        credits: 'credits',
        note: 'toelichting',
        total: 'totaal',
        empty: 'niets te rekenen',
        loading: 'laden…',
        storage: 'opslag per dag (de piek telt)',
        noStorage: 'geen opslagmetingen in deze maand',
        titleDomain: 'kosten per maand',
        infoDomain: 'welke personen met welke functie, hoeveel volle dagen, opslag en korting meetellen in de afschrijving van een maand.',
    };

    readonly title$$ = computed(() => this.input_dauGuid$$() ? this._text.titleDomain : this._text.title);
    readonly info$$ = computed(() => this.input_dauGuid$$() ? this._text.infoDomain : this._text.info);

    constructor() {
        this.control_period.valueChanges
            .pipe(takeUntilDestroyed(this.__destroyRef))
            .subscribe(() => this.__load());

        // Ander domain gekozen: opnieuw rekenen (zelfde maand).
        effect(() => {
            this.__key$$();
            untracked(() => this.__load());
        });
    }

    private __load(): void {
        const __key = this.__key$$();
        const __dauGuid = this.input_dauGuid$$();
        const __period = this.control_period.value?.[autocomplete.itemId] as string | undefined;
        this.__result$$.set(null);
        if (!__key) return;

        this.__loading$$.set(true);
        const __request = __dauGuid
            ? this.__apiService.GET('/api/DomainCredits/dry-run', { params: { query: { domainAUserGuid: __dauGuid, period: __period } } } as any)
            : this.__apiService.GET('/api/Billing/dry-run', { params: { query: { domainGuid: __key, period: __period } } } as any);
        from(__request).pipe(
            unwrapData(),
            catchError(() => EMPTY),
            finalize(() => this.__loading$$.set(false)),
        ).subscribe(data => {
            const __result = ((data as any)?.modelDto ?? null) as IBillingDryRunDto | null;
            // Alleen het antwoord op de huidige keuze tonen.
            if (__key === this.__key$$()) this.__result$$.set(__result);
        });
    }
}
